import React, { useState, useRef } from 'react';
import { NavLink, Link, useLocation } from 'react-router-dom';
import { COMPANY_INFO, MENU_STRUCTURE } from '../data/scrapeData';

export default function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [ydelserDropdownOpen, setYdelserDropdownOpen] = useState(false);
  const [mobileYdelserOpen, setMobileYdelserOpen] = useState(false);
  const dropdownTimeoutRef = useRef(null);
  const location = useLocation();

  const handleDropdownMouseEnter = () => {
    if (dropdownTimeoutRef.current) {
      clearTimeout(dropdownTimeoutRef.current);
    }
    setYdelserDropdownOpen(true);
  };

  const handleDropdownMouseLeave = () => {
    dropdownTimeoutRef.current = setTimeout(() => {
      setYdelserDropdownOpen(false);
    }, 150);
  };

  const isYdelserActive = location.pathname.startsWith('/ydelser') ||
    [
      '/vognmandskorsel-2',
      '/kranarbejde-2',
      '/grapning-af-slam-og-flydelag',
      '/containerudlejning-service',
      '/kornkorsel',
      '/entreprenor-nedbrydningsopgaver',
      '/modtagelse-af-byggeaffald',
      '/grus-udlaegnings-korsel',
      '/halm-korsel',
      '/stammetraes-korsel',
      '/jernplade-leje',
      '/sorterings-anlaeg',
      '/gylletrailer-39-m3',
      '/buffer-kasse-100-m3',
      '/stobningsarbejde',
      '/anlaegsopgaver',
      '/kloak-og-jordarbejde'
    ].some(p => location.pathname.startsWith(p));

  return (
    <header className="w-full bg-black text-white border-b border-white/10 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 py-3.5 flex items-center justify-between gap-4">
        {/* Brand Logo */}
        <Link to="/" className="font-bold text-base sm:text-lg text-white hover:opacity-90 transition leading-tight">
          <span className="block">Vognmand &amp; Entreprenør</span>
          <span className="block font-semibold text-sm sm:text-base text-neutral-300">DBM Aps</span>
        </Link>

        {/* Desktop Navigation Links (Visible >= 1200px) */}
        <nav className="hidden min-[1200px]:flex items-center gap-6 xl:gap-8 text-sm font-medium">
          <NavLink
            to="/"
            end
            className={({ isActive }) =>
              isActive
                ? 'text-white border-b-2 border-white pb-0.5 font-semibold whitespace-nowrap'
                : 'text-white/80 hover:text-white transition-colors whitespace-nowrap'
            }
          >
            Forside
          </NavLink>

          {/* Ydelser Dropdown */}
          <div
            className="relative py-1"
            onMouseEnter={handleDropdownMouseEnter}
            onMouseLeave={handleDropdownMouseLeave}
          >
            <NavLink
              to="/ydelser/"
              className={
                isYdelserActive
                  ? 'text-white border-b-2 border-white pb-0.5 font-semibold flex items-center gap-1.5 whitespace-nowrap'
                  : 'text-white/80 hover:text-white transition-colors flex items-center gap-1.5 whitespace-nowrap'
              }
            >
              <span>Ydelser</span>
              <i className="fa-solid fa-chevron-down text-xs" aria-hidden="true"></i>
            </NavLink>

            {/* Dropdown Menu */}
            {ydelserDropdownOpen && (
              <div className="absolute top-full left-0 w-72 pt-1 z-50">
                <div className="bg-neutral-900 border border-white/10 shadow-2xl py-2">
                  {MENU_STRUCTURE[1].children.map((child, index) => (
                    <NavLink
                      key={index}
                      to={child.href}
                      className={({ isActive }) =>
                        `block px-4 py-2.5 text-xs font-medium transition-colors ${
                          isActive
                            ? 'bg-neutral-800 text-white font-bold border-l-2 border-white pl-3'
                            : 'text-white/80 hover:bg-neutral-800 hover:text-white'
                        }`
                      }
                    >
                      {child.label}
                    </NavLink>
                  ))}
                </div>
              </div>
            )}
          </div>

          <NavLink
            to="/maskiner/"
            className={({ isActive }) =>
              isActive
                ? 'text-white border-b-2 border-white pb-0.5 font-semibold whitespace-nowrap'
                : 'text-white/80 hover:text-white transition-colors whitespace-nowrap'
            }
          >
            Maskiner/Materialer
          </NavLink>

          <NavLink
            to="/om-os/"
            className={({ isActive }) =>
              isActive
                ? 'text-white border-b-2 border-white pb-0.5 font-semibold whitespace-nowrap'
                : 'text-white/80 hover:text-white transition-colors whitespace-nowrap'
            }
          >
            Om os
          </NavLink>

          <NavLink
            to="/kontakt/"
            className={({ isActive }) =>
              isActive
                ? 'text-white border-b-2 border-white pb-0.5 font-semibold whitespace-nowrap'
                : 'text-white/80 hover:text-white transition-colors whitespace-nowrap'
            }
          >
            Kontakt
          </NavLink>
        </nav>

        {/* Contact Info Meta (Desktop) */}
        <div className="hidden min-[1280px]:flex items-center text-xs text-white/90 font-sans tracking-tight shrink-0">
          <a href={`mailto:${COMPANY_INFO.email}`} className="hover:text-white transition-colors whitespace-nowrap">
            {COMPANY_INFO.email}
          </a>
          <span className="mx-2.5 text-white/40">|</span>
          <a href={`tel:${COMPANY_INFO.phoneRaw}`} className="hover:text-white transition-colors whitespace-nowrap">
            Telefon: {COMPANY_INFO.phone}
          </a>
        </div>

        {/* Mobile Burger Menu Button (Visible < 1200px) */}
        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="min-[1200px]:hidden p-2 text-white hover:text-white/80 focus:outline-hidden text-2xl"
          aria-label="Åbn menu"
        >
          <i className={`fa-solid ${mobileMenuOpen ? 'fa-xmark' : 'fa-bars'}`}></i>
        </button>
      </div>

      {/* Mobile Drawer (Visible < 1200px when open) */}
      {mobileMenuOpen && (
        <div className="min-[1200px]:hidden bg-neutral-950 text-white border-t border-white/10 px-6 py-4 space-y-3 z-50 animate-fade-in max-h-[calc(100vh-75px)] overflow-y-auto">
          <NavLink
            to="/"
            end
            onClick={() => setMobileMenuOpen(false)}
            className={({ isActive }) =>
              `block py-2 text-sm font-medium ${isActive ? 'text-white font-bold underline' : 'text-white/80 hover:text-white'}`
            }
          >
            Forside
          </NavLink>

          <div>
            <div className="flex justify-between items-center py-2 border-b border-white/10">
              <NavLink
                to="/ydelser/"
                onClick={() => setMobileMenuOpen(false)}
                className={`text-sm font-medium ${isYdelserActive ? 'text-white font-bold underline' : 'text-white/80 hover:text-white'}`}
              >
                Ydelser
              </NavLink>
              <button
                onClick={() => setMobileYdelserOpen(!mobileYdelserOpen)}
                className="p-1 text-white/60 hover:text-white"
              >
                <i className={`fa-solid ${mobileYdelserOpen ? 'fa-chevron-up' : 'fa-chevron-down'}`}></i>
              </button>
            </div>

            {(mobileYdelserOpen || isYdelserActive) && (
              <div className="pl-4 py-2 space-y-2 border-l border-white/20 my-2">
                {MENU_STRUCTURE[1].children.map((child, index) => (
                  <NavLink
                    key={index}
                    to={child.href}
                    onClick={() => setMobileMenuOpen(false)}
                    className={({ isActive }) =>
                      `block py-1 text-xs ${isActive ? 'text-white font-bold' : 'text-white/70 hover:text-white'}`
                    }
                  >
                    {child.label}
                  </NavLink>
                ))}
              </div>
            )}
          </div>

          <NavLink
            to="/maskiner/"
            onClick={() => setMobileMenuOpen(false)}
            className={({ isActive }) =>
              `block py-2 text-sm font-medium ${isActive ? 'text-white font-bold underline' : 'text-white/80 hover:text-white'}`
            }
          >
            Maskiner/Materialer
          </NavLink>

          <NavLink
            to="/om-os/"
            onClick={() => setMobileMenuOpen(false)}
            className={({ isActive }) =>
              `block py-2 text-sm font-medium ${isActive ? 'text-white font-bold underline' : 'text-white/80 hover:text-white'}`
            }
          >
            Om os
          </NavLink>

          <NavLink
            to="/kontakt/"
            onClick={() => setMobileMenuOpen(false)}
            className={({ isActive }) =>
              `block py-2 text-sm font-medium ${isActive ? 'text-white font-bold underline' : 'text-white/80 hover:text-white'}`
            }
          >
            Kontakt
          </NavLink>

          <div className="pt-4 border-t border-white/10 text-xs text-white/80 space-y-1">
            <div><a href={`mailto:${COMPANY_INFO.email}`} className="whitespace-nowrap">{COMPANY_INFO.email}</a></div>
            <div><a href={`tel:${COMPANY_INFO.phoneRaw}`} className="whitespace-nowrap">Telefon: {COMPANY_INFO.phone}</a></div>
          </div>
        </div>
      )}
    </header>
  );
}
